/**
 * The wire shapes of the server-side import job. [AVP-891]
 *
 * These mirror asset-manager's ImportJobController.toDto and the
 * /api/uploads and /api/assetIcons/batch responses. Keep them in step.
 */

export type TImportJobState =
  | 'uploading'
  | 'validating'
  | 'validated'
  | 'running'
  | 'done'
  | 'failed'
  | 'cancelled';

/** States in which the job is still doing something and is worth polling. */
export const LIVE_JOB_STATES: TImportJobState[] = ['validating', 'validated', 'running'];

export const TERMINAL_JOB_STATES: TImportJobState[] = ['done', 'failed', 'cancelled'];

export const isLiveJob = (state: TImportJobState): boolean => LIVE_JOB_STATES.includes(state);

export const isTerminalJob = (state: TImportJobState): boolean => (
  TERMINAL_JOB_STATES.includes(state)
);

export interface IImportJob {
  jobId: string;
  type: string;
  state: TImportJobState;
  fileName: string | null;
  onDuplicate: string;
  /** rows the file turned out to have; 0 until phase A finishes counting */
  total: number;
  processed: number;
  created: number;
  updated: number;
  skipped: number;
  failed: number;
  errorCount: number;
  /** the report itself was capped; say so rather than implying it is complete */
  errorTruncated: boolean;
  cancelRequested: boolean;
  ratePerSecond: number;
  message: string | null;
  createdAt: number;
  startedAt: number | null;
  finishedAt: number | null;
  elapsedMs: number;
  fractionComplete: number;
  estimatedMsRemaining: number | null;
}

export interface IImportJobList {
  jobs: IImportJob[];
}

export type TImportProblemStatus = 'invalid' | 'duplicate' | 'failed';

export interface IImportProblem {
  /** physical line in the uploaded file, so it matches what the user sees */
  lineNo: number | null;
  code: string | null;
  status: TImportProblemStatus;
  message: string | null;
}

export interface IImportProblemPage {
  total: number;
  truncated: boolean;
  skip: number;
  limit: number;
  rows: IImportProblem[];
}

export interface IStagedUpload {
  uploadId: string;
  /** dictated by the server; a client-chosen part size could exceed nginx's cap */
  partSize: number;
  partCount: number;
  receivedParts: number[];
  fileName?: string;
  state?: string;
  totalBytes?: number;
}

export interface IImportJobPlanColumn {
  index: number;
  /** the header text the browser saw there; the server refuses a mismatch */
  name: string;
  /** property name to import under; absent for code and description */
  as?: string;
}

export interface IImportJobPlan {
  delimiter: string;
  codeColumn: IImportJobPlanColumn;
  descriptionColumn: IImportJobPlanColumn;
  propertyColumns: IImportJobPlanColumn[];
}

export type TIconBatchStatus = 'created' | 'updated' | 'failed';

export interface IAssetIconBatchEntry {
  code: string;
  contentType: string;
  /** base64, no data: prefix */
  data: string;
}

export interface IAssetIconBatchResult {
  code: string;
  status: TIconBatchStatus;
  message?: string;
}

export interface IAssetIconBatchResponse {
  total: number;
  created: number;
  updated: number;
  failed: number;
  results: IAssetIconBatchResult[];
}
